import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, clearToken, getToken, setToken } from '@/lib/api';

export interface AuthUser {
  can_upload_material?: boolean;
  portal_scope?: 'full' | 'doctor' | 'uploads';
  portal_talent_id?: string | null;
  portal_talent_ids?: string[];
  phone?: string | null;
  id: string;
  email: string;
  display_name: string;
  photo_url?: string | null;
  role: 'owner' | 'manager' | 'agent' | 'crew' | 'freelancer' | 'client';
  /** present (true) only on the three partners' own sign-in answer */
  partner?: boolean;
  designations: string[];
  client_id?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthCtx = createContext<AuthState>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    // Only a 401 means the sign-in is over. A slow or busy server (timeout, 502) used to
    // clear the token too, and on 30 Oct a loaded box signed everyone out on every
    // reload. Now it keeps the token and asks again, backing off up to 15 s.
    for (let attempt = 0; ; attempt++) {
      try {
        const r = await api.get<AuthUser>('/auth/me');
        setUser(r.data);
        break;
      } catch (e) {
        const status = (e as { response?: { status?: number } }).response?.status;
        if (status === 401 || !getToken()) {
          clearToken();
          setUser(null);
          break;
        }
        await new Promise((res) => setTimeout(res, Math.min(15_000, 2_000 * 2 ** attempt)));
      }
    }
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    window.location.href = '/login';
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

// Helper for the OAuth-callback page
export async function exchangeCode(code: string): Promise<void> {
  const r = await api.post<{ access_token: string; user: AuthUser }>(
    '/auth/google/callback',
    { code }
  );
  setToken(r.data.access_token);
}

// Email + password sign-in. Returns void; throw on bad creds (axios bubbles 401).
export async function passwordLogin(email: string, password: string): Promise<void> {
  const r = await api.post<{ access_token: string; user: AuthUser }>(
    '/auth/login',
    { email, password }
  );
  setToken(r.data.access_token);
}
